import React from "react";
import { Link } from "react-router-dom";
import RetreatListing from "../components/RetreatListing";
import TrendingDestinations from "../components/TrendingDestinations";
import PopularCourses from "../components/PopularCourses";
import { BookOpen, Sparkles, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import Seo from "../components/Seo";
import { HERO_IMAGE_URL } from "../lib/heroImage";
import { isPrerendered } from "../lib/hydration";
import { DEDICATED_PAGE_TYPES } from "../lib/courseTypes";

function Home() {
  const faqItems = [
    {
      question: "How do I book yoga retreats in India with YogaRetreatsBooking?",
      answer:
        "Use the filters to shortlist retreats by location, style, duration, dates, meals, and reviews. Compare options and then click the retreat details link to book with the provider.",
    },
    {
      question: "Do you list yoga teacher training retreats and courses in India?",
      answer:
        "Yes. We list teacher training retreats and YTT courses in India, including 100-hour, 200-hour and 300-hour programs. Browse our dedicated Yoga Teacher Training page to compare batches, pricing, and locations like Rishikesh, Goa and Kerala.",
    },
    {
      question: "Can I find online yoga courses?",
      answer:
        "Yes. We list online yoga courses you can join from anywhere. Visit our Online Courses page to compare virtual programs by style, duration, rating and price.",
    },
    {
      question: "Can I find meditation retreats and wellness retreats too?",
      answer:
        "Yes. You can browse meditation retreats and wellness retreats across India and filter by destination, retreat type, duration, rating and budget.",
    },
    {
      question: "Which destinations are most popular for yoga retreats in India?",
      answer:
        "Rishikesh is a top choice for traditional yoga retreats, Goa is popular for beach retreats, and Kerala is known for Ayurveda and wellness retreats. Use the Location filter to explore all destinations.",
    },
  ];

  const homeJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        name: "Yoga Retreats Booking",
        url: "https://yogaretreatsbooking.com/",
        logo: "https://yogaretreatsbooking.com/favicon.ico",
        sameAs: [],
      },
      {
        "@type": "WebSite",
        name: "Yoga Retreats Booking",
        url: "https://yogaretreatsbooking.com/",
        potentialAction: {
          "@type": "SearchAction",
          target: "https://yogaretreatsbooking.com/?q={search_term_string}",
          "query-input": "required name=search_term_string",
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: faqItems.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.answer,
          },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pt-16">
      <Seo
        title="Yoga Retreats in India"
        description="Yoga retreats booking made easy. Find yoga retreats, meditation retreats, and wellness programs in India. Compare locations, dates, meals, reviews and prices."
        canonicalPath="/"
        ogImagePath="/favicon.ico"
        jsonLd={homeJsonLd}
      />
      {/* Hero Section */}
      <div className="relative h-[400px] bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE_URL}
            alt="Yoga meditation retreat in India"
            className="w-full h-full object-cover opacity-60"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/50 to-gray-900" />
        </div>

        <div className="relative container mx-auto px-4 h-full flex flex-col justify-center items-center text-center">
          <motion.div
            initial={isPrerendered ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-bold uppercase tracking-widest mb-4 border border-blue-500/30 backdrop-blur-md">
              <Sparkles size={14} /> Yoga in India
            </span>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Yoga Retreats in India <span className="inline-block">Made Easy</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
              Yoga retreats booking for meditation retreats and wellness programs across India. Compare locations, dates, meals, reviews and prices in one place. For teacher training or online courses, visit our dedicated pages.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-10 relative z-10 pb-20">
        {/* Trending Destinations (single line of quick links) */}
        <div className="bg-white rounded-3xl p-6 md:p-8 mb-8 shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <div className="flex items-center gap-2 shrink-0">
              <TrendingUp size={20} className="text-emerald-600" />
              <h2 className="text-lg md:text-xl font-black text-gray-900">Trending Destinations in India</h2>
            </div>
            <TrendingDestinations />
          </div>
        </div>

        <div className="bg-white rounded-3xl p-6 md:p-8 mb-8 shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
            <div className="flex items-center gap-2 shrink-0">
              <BookOpen size={20} className="text-emerald-600" />
              <h2 className="text-lg md:text-xl font-black text-gray-900">
                Yoga Teacher Training, Prenatal Yoga & Online Courses
              </h2>
            </div>
            <PopularCourses />
          </div>
        </div>

        <RetreatListing
          pageSize={12}
          excludedTypes={DEDICATED_PAGE_TYPES}
          heading="Yoga Retreats & Meditation Retreats in India"
          subheading="Compare retreats and wellness programs by destination, style, duration, rating and price"
        />

        <section className="mt-12 bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 mb-4">Yoga Retreats Booking for India: Yoga, Meditation & Wellness</h2>
          <p className="text-gray-600 leading-relaxed">
            YogaRetreatsBooking is built for travelers looking to book yoga retreats in India. Discover meditation retreats and wellness retreats across destinations like Rishikesh, Goa, Kerala and the Himalayas. Use filters to compare retreat styles, duration, dates, meals, reviews and prices. For Yoga Teacher Training, online courses, or prenatal yoga, explore our dedicated sections.
          </p>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Retreats You Can Compare</h3>
              <ul className="text-gray-600 text-sm space-y-1">
                <li>Yoga retreats in India (beginner to advanced)</li>
                <li>Meditation retreats and mindfulness programs</li>
                <li>Wellness retreats and Ayurveda programs</li>
                <li>
                  <Link to="/yoga-teacher-training" className="text-emerald-600 hover:underline">
                    Yoga Teacher Training (100/200/300-hour YTT)
                  </Link>
                </li>
                <li>
                  <Link to="/online-courses" className="text-emerald-600 hover:underline">
                    Online yoga courses
                  </Link>
                </li>
                <li>
                  <Link to="/prenatal-yoga" className="text-emerald-600 hover:underline">
                    Prenatal yoga teacher training
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Top Destinations in India</h3>
              <ul className="text-gray-600 text-sm space-y-1">
                <li>Rishikesh (Yoga Capital of the World)</li>
                <li>Goa (beach retreats)</li>
                <li>Kerala (Ayurveda and wellness)</li>
                <li>Dharamshala and the Himalayas</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">How to Choose & Book</h3>
              <ul className="text-gray-600 text-sm space-y-1">
                <li>Pick a goal: yoga, meditation, or wellness</li>
                <li>Check certification, ratings and reviews</li>
                <li>Compare dates, availability and inclusions</li>
                <li>Choose your best match and book with the provider</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mt-8 bg-white rounded-3xl p-8 shadow-sm border border-gray-100">
          <h2 className="text-2xl font-black text-gray-900 mb-4">FAQs about Yoga Retreats in India</h2>
          <div className="space-y-4">
            {faqItems.map((item) => (
              <details key={item.question} className="group rounded-2xl border border-gray-100 p-4">
                <summary className="cursor-pointer font-bold text-gray-900">{item.question}</summary>
                <p className="mt-2 text-gray-600 text-sm leading-relaxed">{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Home;
